import { useEffect, useRef } from 'react';
import { databases, DATABASE_ID, USERS_COLLECTION_ID } from '../config/appwrite';

/**
 * Custom hook for presence/online tracking with heartbeat mechanism
 *
 * Updates user's last_active timestamp directly in the database
 * to maintain user's online status while authenticated.
 *
 * @param isAuthenticated - Whether the user is currently authenticated
 * @param userId - User ID to update
 * @param intervalMs - Heartbeat interval in milliseconds (default: 60000ms = 60s)
 */
export const usePresenceHeartbeat = (
  isAuthenticated: boolean,
  userId?: string,
  intervalMs: number = 60000
) => {
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const isActiveRef = useRef<boolean>(false);

  useEffect(() => {
    // Debug logging
    console.log('🔵 Presence Heartbeat Hook:', {
      isAuthenticated,
      userId,
      intervalMs,
    });

    // Only start heartbeat if user is authenticated and has ID
    if (!isAuthenticated || !userId) {
      console.log('⚠️ Heartbeat not started - user not authenticated or no user ID');
      return;
    }

    console.log('✅ Starting heartbeat for user:', userId);
    // Mark heartbeat as active
    isActiveRef.current = true;

    // Heartbeat function - directly update database
    const sendHeartbeat = async () => {
      // Double-check we're still active
      if (!isActiveRef.current) {
        return;
      }

      try {
        console.log('💓 Updating last_active for user:', userId);

        await databases.updateDocument(DATABASE_ID, USERS_COLLECTION_ID, userId, {
          last_active: new Date().toISOString(),
        });

        console.log('✅ Heartbeat successful!');
      } catch (error: any) {
        // Silently handle errors without breaking the app
        console.error('❌ Heartbeat error:', error.message || error);
      }
    };

    // Send initial heartbeat immediately
    sendHeartbeat();

    // Set up interval for subsequent heartbeats
    intervalRef.current = setInterval(sendHeartbeat, intervalMs);

    // Cleanup function
    return () => {
      isActiveRef.current = false;

      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isAuthenticated, userId, intervalMs]);

  // Expose cleanup function for manual stopping
  const stopHeartbeat = () => {
    isActiveRef.current = false;

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  return { stopHeartbeat };
};
